import * as React from 'react';
import { useEffect, useState } from 'react';
import { useCommonUIActionsContext } from '../../../contexts/CommonUIActionsContext';
import { NO_DATA_FOUND_LABEL } from '../../../constants';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import PlayerCard from './components/PlayerCard';
import useFetchPreRegistrationData from '@r-and-a-shared-ui/common-ui/src/hooks/useFetchPreRegistrationData';
import {
  CircularProgressSize,
  StackSpacing,
} from '../PlayerPortalsOnSiteRegistrationAdminPanel/PlayerPortalsOnSiteRegistrationAdminPanel';
import { getPPContactToDisplay } from '@r-and-a-shared-ui/common-ui';
import { PlayerPortalsForm } from 'libs/common-forms-ui/src/lib/PlayerPortals/PlayerPortalsForm/PlayerPortalsForm';
import { SidePanel } from '../../SidePanel/SidePanel';

import styles from '../PlayerPortalsOnSiteRegistrationAdminPanel/PlayerPortalsOnSiteRegistrationAdminPanel.module.scss';
import stylesCard from './components/PlayerCard.module.scss';

type ManagerPanelProps = {
  title: string;
  formComponent: any;
  resource?: any;
  kontentItemId?: string;
};

const PAGE_SIZE = 16;

export const PreRegistrationManagerPanel = (props: ManagerPanelProps) => {
  const { title, formComponent, resource, kontentItemId } = props;
  const [allParticipants, setAllParticipants] = React.useState<any[]>([]);
  const [displayedParticipants, setDisplayedParticipants] = React.useState<
    any[]
  >([]);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<object | null>(
    null,
  );

  const actionContext = useCommonUIActionsContext();
  const { preRegistrationAPI } = actionContext || {};

  const [page, setPage] = useState(1);

  const paginatedParticipants = React.useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return displayedParticipants.slice(startIndex, startIndex + PAGE_SIZE);
  }, [displayedParticipants, page]);

  const ppContact = getPPContactToDisplay();

  const { data, loading, hasFetched } = useFetchPreRegistrationData(
    preRegistrationAPI,
    ppContact?.id,
    resource,
  );

  useEffect(() => {
    if (data?.items) {
      setAllParticipants(data.items);
      setDisplayedParticipants(data.items);
    }
  }, [data]);

  return (
    <div className={styles.container} data-kontent-item-id={kontentItemId}>
      <div className={styles.toolbar}>
        <h1 className={styles.title}>
          {title}{' '}
          <span className={stylesCard.recordBadge}>
            {allParticipants.length} record
            {allParticipants.length > 1 ? 's' : ''}
          </span>
        </h1>
      </div>

      {loading || !hasFetched ? (
        <div className={styles.fetchStatus}>
          <CircularProgress size={CircularProgressSize} />
        </div>
      ) : paginatedParticipants.length === 0 ? (
        <div className={styles.fetchStatus}>{NO_DATA_FOUND_LABEL}</div>
      ) : (
        <div className={styles.participantsContainer}>
          <div className={styles.participantsList}>
            {paginatedParticipants.map(participant => (
              <PlayerCard
                key={participant.id}
                name={participant.name}
                onClick={() => {
                  setSelectedParticipant(participant);
                  setIsPanelOpen(true);
                }}
              />
            ))}
          </div>
          {displayedParticipants.length > PAGE_SIZE && (
            <div className={styles.pagination}>
              <Stack spacing={StackSpacing}>
                <Pagination
                  color="primary"
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  count={Math.ceil(displayedParticipants.length / PAGE_SIZE)}
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </div>
          )}
        </div>
      )}
      {isPanelOpen && selectedParticipant && (
        <SidePanel
          children={
            <PlayerPortalsForm
              key={(selectedParticipant as any)?.id}
              {...formComponent}
              customId={(selectedParticipant as any)?.id}
            />
          }
          isPanelOpen={isPanelOpen}
          setIsPanelOpen={setIsPanelOpen}
          items={allParticipants}
          selectedItem={selectedParticipant}
          setSelectedItem={setSelectedParticipant}
        />
      )}
    </div>
  );
};

export default PreRegistrationManagerPanel;
